import { ChangeEventHandler, FC, KeyboardEventHandler, useEffect, useState } from 'react';
import {
  ALLOWED_WIDGET_HOSTS,
  AdElementWidget,
  WidgetUrlRejection,
  resolveWidgetUrl,
} from '@adflow/types';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react';
import { useStore } from '../../../hooks/useStore';

const URL_PROBLEMS: Record<WidgetUrlRejection, string> = {
  'empty-url': 'Paste the widget link. The template cannot be saved without it.',
  'invalid-url': 'This is not a valid link.',
  'not-https': 'The link must start with https://.',
  'has-credentials': 'The link must not contain a user name or password.',
  'non-default-port': 'The link must not contain a port number.',
  'host-not-allowed': `Widgets can only be loaded from ${ALLOWED_WIDGET_HOSTS.join(', ')}.`,
  'empty-param-name': 'Every widget parameter needs a name.',
};

type WidgetControlsProps = {
  selectedElement: AdElementWidget;
};

const WidgetControls: FC<WidgetControlsProps> = ({ selectedElement }) => {
  const updateElementData = useStore((state) => state.updateElementData);
  const { data } = selectedElement;
  // The typed link reaches the layer when the field loses focus or on Enter, not per keystroke:
  // the canvas draws the layer, and every new link reloads the widget page in its iframe.
  // Clicking Save takes the focus first, so a typed link is always saved.
  const [draftUrl, setDraftUrl] = useState(data.url);
  useEffect(() => setDraftUrl(data.url), [data.url]);
  // The same rule the served ad applies, so the reason shown here is why saving is refused.
  const resolved = resolveWidgetUrl({ ...data, url: draftUrl });

  const handleUrlChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setDraftUrl(event.target.value);
  };

  const commitUrl = () => {
    if (draftUrl !== data.url) {
      updateElementData(selectedElement.id, { ...data, url: draftUrl });
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') commitUrl();
  };

  return (
    <Flex direction="column" gap={6}>
      <FormControl isInvalid={!resolved.ok}>
        <FormLabel htmlFor="widget-url" fontSize="lg" fontWeight="semibold">
          Widget link
        </FormLabel>
        <Input
          id="widget-url"
          data-test-id="widget-url-input"
          placeholder={`https://${ALLOWED_WIDGET_HOSTS[0]}/…`}
          value={draftUrl}
          onChange={handleUrlChange}
          onBlur={commitUrl}
          onKeyDown={handleKeyDown}
        />
        {!resolved.ok && (
          <FormErrorMessage data-test-id="widget-url-problem">
            {URL_PROBLEMS[resolved.reason]}
          </FormErrorMessage>
        )}
      </FormControl>
      {data.params.length > 0 && (
        <Flex direction="column">
          <Heading fontSize="lg" fontWeight="semibold" mb="2">
            Parameters
          </Heading>
          <List spacing={1} data-test-id="widget-params">
            {data.params.map((param) => (
              <ListItem key={param.name}>
                <Text as="span" fontFamily="mono" fontSize="sm">
                  {param.name} = {param.value}
                </Text>
              </ListItem>
            ))}
          </List>
        </Flex>
      )}
    </Flex>
  );
};

WidgetControls.displayName = 'WidgetControls';
export default WidgetControls;
